import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import PeopleOutlinedIcon from '@mui/icons-material/PeopleOutlined';
import PhoneInTalkOutlinedIcon from '@mui/icons-material/PhoneInTalkOutlined';
import RateReviewOutlinedIcon from '@mui/icons-material/RateReviewOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import WorkOutlineOutlinedIcon from '@mui/icons-material/WorkOutlineOutlined';
import { Box, Divider, List, ListItemButton, ListItemIcon, ListItemText, Tooltip, Typography } from '@mui/material';
import { NavLink } from 'react-router-dom';
import type { Role } from '@/shared/api/types';
import { BrandLogo } from '@/shared/components/BrandLogo';
import { elevation } from '@/theme';
import { NavAccount } from './NavAccount';

const ALL: Role[] = ['admin', 'recruiter', 'hiring_manager'];
const GROUPS = [
  { label: 'Hiring', items: [
    { to: '/', label: 'Overview', icon: DashboardOutlinedIcon, roles: ALL },
    { to: '/jobs', label: 'Jobs', icon: WorkOutlineOutlinedIcon, roles: ALL },
    { to: '/applicants', label: 'Applicants', icon: PeopleOutlinedIcon, roles: ALL },
    { to: '/wellfound', label: 'Wellfound openings', icon: LinkOutlinedIcon, roles: ALL },
  ] },
  { label: 'Calls', items: [
    { to: '/live', label: 'Live calls', icon: PhoneInTalkOutlinedIcon, roles: ['admin', 'recruiter'] },
    { to: '/review', label: 'Review', icon: RateReviewOutlinedIcon, roles: ['admin', 'recruiter'] },
  ] },
  { label: 'Workspace', items: [
    { to: '/analytics', label: 'Analytics', icon: InsightsOutlinedIcon, roles: ['admin'] },
    { to: '/settings', label: 'Settings', icon: SettingsOutlinedIcon, roles: ALL },
  ] },
] as const;

/** Full width on wide screens and in the phone drawer; icons only on laptops and tablets, where the page needs the room. */
const RAIL_WIDTH = { full: 236, compact: 72 };

interface Props { role: Role; name: string; onSignOut: () => void; onNavigate?: () => void; compact?: boolean }

function Brand({ compact }: { compact: boolean }) {
  if (compact) return <Box sx={{ py: 2.5, display: 'flex', justifyContent: 'center' }}><Box component="img" src="/ai-planet-logo.png" alt="AI Planet" sx={{ width: 44, height: 'auto' }} /></Box>;
  return (
    <Box sx={{ px: 2.5, pt: 3, pb: 2 }}>
      <Typography component="p" sx={{ fontSize: '1.375rem', fontWeight: 700, lineHeight: 1, letterSpacing: '-0.035em' }}>Screenline</Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 1 }}>
        <Typography variant="caption" sx={{ color: 'text.disabled' }}>by</Typography>
        <BrandLogo height={16} />
      </Box>
    </Box>
  );
}

export function NavRail({ role, name, onSignOut, onNavigate, compact = false }: Props) {
  const groups = GROUPS.map((g) => ({ ...g, items: g.items.filter((i) => (i.roles as readonly Role[]).includes(role)) })).filter((g) => g.items.length);
  return (
    <Box component="nav" aria-label="Main" sx={{ width: compact ? RAIL_WIDTH.compact : RAIL_WIDTH.full, height: '100%', display: 'flex', flexDirection: 'column', bgcolor: 'background.default', borderRight: 1, borderColor: 'divider' }}>
      <Brand compact={compact} />
      <Box sx={{ px: 1.25, flex: 1, overflowY: 'auto' }}>
        {groups.map((g, gi) => (
          <Box key={g.label} sx={{ mt: gi ? 1.5 : 0.5 }}>
            {compact ? gi > 0 && <Divider sx={{ mx: 1.5, mb: 1.5 }} /> : <Typography variant="overline" sx={{ display: 'block', px: 1.25, pb: 0.5, color: 'text.disabled' }}>{g.label}</Typography>}
            <List disablePadding>
              {g.items.map((item) => (
                <Tooltip key={item.to} title={compact ? item.label : ''} placement="right">
                  <ListItemButton component={NavLink} to={item.to} end={item.to === '/'} onClick={onNavigate} aria-label={compact ? item.label : undefined}
                    sx={(t) => ({
                      mb: 0.25, minHeight: 40, px: 1.25, justifyContent: compact ? 'center' : 'flex-start', color: 'text.secondary',
                      '&:hover': { bgcolor: 'action.hover', color: 'text.primary' },
                      '&.active': { bgcolor: 'background.paper', color: 'text.primary', boxShadow: elevation.inset, '& .MuiListItemIcon-root': { color: t.palette.secondary.main } },
                    })}>
                    <ListItemIcon sx={{ minWidth: compact ? 0 : 34 }}><item.icon sx={{ fontSize: 19 }} /></ListItemIcon>
                    {!compact && <ListItemText primary={item.label} slotProps={{ primary: { variant: 'body2', sx: { fontWeight: 500 } } }} />}
                  </ListItemButton>
                </Tooltip>
              ))}
            </List>
          </Box>
        ))}
      </Box>
      <NavAccount name={name} role={role} compact={compact} onSignOut={onSignOut} />
    </Box>
  );
}
